import React, { useEffect, useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";

/* Shared design tokens + primitives for the Placement module — same palette
   family as the IT Support / Checklist modules so it reads as one product. */
export const T = {
  bg: "#f4f6f8",
  white: "#ffffff",
  border: "#e4e8ee",
  borderStrong: "#d7dce4",
  shadow: "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
  shadowMd: "0 12px 32px -8px rgba(16,24,40,0.22)",
  radius: 14,
  ink1: "#101828",
  ink2: "#475467",
  ink3: "#98a2b3",
  primary: "#6366f1",
  primaryDark: "#4f46e5",
  primarySoft: "#eef2ff",
  amber: "#f59e0b",
  amberSoft: "#fffbeb",
  green: "#16a34a",
  greenSoft: "#f0fdf4",
  cyan: "#0891b2",
  cyanSoft: "#ecfeff",
  purple: "#7c3aed",
  purpleSoft: "#f5f3ff",
  teal: "#0d9488",
  tealSoft: "#f0fdfa",
  danger: "#e11d48",
  dangerSoft: "#fef2f2",
  fontDisplay: "'Space Grotesk', 'Segoe UI', ui-sans-serif, sans-serif",
  fontBody: "'Inter', 'Segoe UI', ui-sans-serif, sans-serif",
  fontMono: "'IBM Plex Mono', ui-monospace, monospace",
};

export const STAGE_OPTIONS = [
  { value: "ENQUIRY", label: "Enquiry" },
  { value: "SCREENING", label: "Screening" },
  { value: "TEST", label: "Test" },
  { value: "INTERVIEW", label: "Interview" },
  { value: "RESULT", label: "Result" },
];

export const SCREENING_OUTCOME_OPTIONS = [
  { value: "PASSED", label: "Shortlisted" },
  { value: "FAILED", label: "Not Shortlisted" },
];

// Enquiry only has one meaningful outcome today (the "Application Received"
// email having gone out) — no pass/fail decision is made at this stage.
export const ENQUIRY_OUTCOME_OPTIONS = [
  { value: "PASSED", label: "Application Received" },
];

export const STAGE_FILTER_OPTIONS = [{ value: "ALL", label: "All Stages" }, ...STAGE_OPTIONS];

export const RESULT_OPTIONS = [
  { value: "SELECTED", label: "Offered" },
  { value: "REJECTED", label: "Rejected" },
  { value: "ON_HOLD", label: "On Hold" },
  { value: "OFFER_DECLINED", label: "Offer Declined" },
  { value: "NOT_SHORTLISTED", label: "Not Shortlisted" },
];

export const OUTCOME_OPTIONS = [
  { value: "PASSED", label: "Passed" },
  { value: "FAILED", label: "Failed" },
  { value: "ON_HOLD", label: "On Hold" },
];

export function labelFor(options, value) {
  return options.find((o) => o.value === value)?.label || value || "—";
}

export function GlobalStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500&display=swap');
      @keyframes plc-spin { to { transform: rotate(360deg); } }
      @keyframes plc-fade-in { from { opacity: 0; transform: translateY(6px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      .plc-row:hover { background: #fafbfc; }
      .plc-master-row:hover { border-color: ${T.borderStrong}; box-shadow: 0 2px 8px rgba(16,24,40,0.06); }
      .plc-btn:focus-visible, .plc-pill:focus-visible { outline: 2px solid ${T.primary}; outline-offset: 2px; }
      .plc-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
      .plc-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
      * { box-sizing: border-box; }
    `}</style>
  );
}

/* Click-outside pill dropdown — no native <select>, matches the rest of the app.
   variant="plain" strips the button's own border/background/padding so several
   of these can sit inside one shared bordered container (e.g. an Hour : Minute
   AM/PM time field) without each segment looking like its own separate box. */
export function PillSelect({ value, options, onChange, placeholder, variant = "default" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find((o) => o.value === value);
  const plain = variant === "plain";

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className="plc-btn"
        onClick={() => setOpen((p) => !p)}
        style={plain ? {
          display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
          width: "100%", padding: "10px 6px", border: "none", background: "transparent",
          color: T.ink1, fontSize: 13.5, fontFamily: T.fontBody, cursor: "pointer",
        } : {
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${T.border}`,
          background: T.white, color: T.ink1, fontSize: 13.5, fontFamily: T.fontBody, cursor: "pointer",
          transition: "border-color .12s ease",
        }}
        onMouseEnter={(e) => { if (!plain) e.currentTarget.style.borderColor = T.borderStrong; }}
        onMouseLeave={(e) => { if (!plain) e.currentTarget.style.borderColor = T.border; }}
      >
        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selected ? selected.label : <span style={{ color: T.ink3 }}>{placeholder}</span>}</span>
        <FaChevronDown size={9} style={{ color: T.ink3, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", flexShrink: 0 }} />
      </button>
      {open && (
        <div style={plain ? {
          position: "absolute", top: "calc(100% + 4px)", left: 0, minWidth: 72, zIndex: 40,
          background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: T.shadowMd,
          maxHeight: 220, overflowY: "auto", padding: 4, animation: "plc-fade-in .12s ease",
        } : {
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 40,
          background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: T.shadowMd,
          maxHeight: 220, overflowY: "auto", padding: 4, animation: "plc-fade-in .12s ease",
        }}>
          {options.map((o) => (
            <div
              key={o.value}
              onClick={() => { onChange(o.value); setOpen(false); }}
              style={{
                padding: "8px 10px", borderRadius: 7, fontSize: 13.5, cursor: "pointer",
                color: o.value === value ? T.primary : T.ink1,
                background: o.value === value ? T.primarySoft : "transparent",
                fontWeight: o.value === value ? 600 : 400,
              }}
              onMouseEnter={(e) => { if (o.value !== value) e.currentTarget.style.background = "#f8fafc"; }}
              onMouseLeave={(e) => { if (o.value !== value) e.currentTarget.style.background = "transparent"; }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Spinner({ size = 30, padding = "60px 0" }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding }}>
      <div style={{
        width: size, height: size, borderRadius: "50%",
        border: `3px solid ${T.border}`, borderTopColor: T.primary,
        animation: "plc-spin 0.7s linear infinite",
      }} />
    </div>
  );
}

const STAGE_STYLE = {
  ENQUIRY:   { bg: T.amberSoft, fg: "#b45309" },
  SCREENING: { bg: T.tealSoft, fg: T.teal },
  TEST:      { bg: T.cyanSoft, fg: T.cyan },
  INTERVIEW: { bg: T.purpleSoft, fg: T.purple },
  RESULT:    { bg: T.primarySoft, fg: T.primaryDark },
};

export function StagePill({ stage }) {
  const s = STAGE_STYLE[stage] || { bg: "#f1f5f9", fg: T.ink2 };
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11.5,
      fontWeight: 700, letterSpacing: ".2px", background: s.bg, color: s.fg, fontFamily: T.fontBody,
      whiteSpace: "nowrap",
    }}>
      {labelFor(STAGE_OPTIONS, stage)}
    </span>
  );
}

const RESULT_STYLE = {
  PENDING:        { bg: "#f1f5f9", fg: T.ink2 },
  SELECTED:       { bg: T.greenSoft, fg: T.green },
  REJECTED:       { bg: T.dangerSoft, fg: T.danger },
  ON_HOLD:        { bg: T.amberSoft, fg: "#b45309" },
  OFFER_DECLINED: { bg: T.dangerSoft, fg: T.danger },
  NOT_SHORTLISTED: { bg: T.dangerSoft, fg: T.danger },
};

export function ResultPill({ result }) {
  const s = RESULT_STYLE[result] || { bg: "#f1f5f9", fg: T.ink2 };
  const label = result === "PENDING" ? "—" : labelFor(RESULT_OPTIONS, result);
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11.5,
      fontWeight: 700, letterSpacing: ".2px", background: s.bg, color: s.fg, fontFamily: T.fontBody,
      whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

const OUTCOME_STYLE = {
  PASSED:  { bg: T.greenSoft, fg: T.green },
  FAILED:  { bg: T.dangerSoft, fg: T.danger },
  ON_HOLD: { bg: T.amberSoft, fg: "#b45309" },
};

// The Result column shows the candidate's final result once decided; until
// then (still in Enquiry/Screening/Test/Interview), it shows THAT stage's
// own outcome instead of sitting blank — Enquiry shows "Application
// Received" once its email is sent, Screening gets Shortlisted/Not
// Shortlisted wording, Test/Interview get Passed/Failed/On Hold.
export function StageOutcomePill({ stage, outcome }) {
  const s = stage === "ENQUIRY" ? { bg: T.primarySoft, fg: T.primaryDark } : (OUTCOME_STYLE[outcome] || { bg: "#f1f5f9", fg: T.ink2 });
  const label = stage === "ENQUIRY"
    ? "Application Received"
    : stage === "SCREENING"
      ? (outcome === "PASSED" ? "Shortlisted" : outcome === "FAILED" ? "Not Shortlisted" : "On Hold")
      : labelFor(OUTCOME_OPTIONS, outcome);
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11.5,
      fontWeight: 700, letterSpacing: ".2px", background: s.bg, color: s.fg, fontFamily: T.fontBody,
      whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

export const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(16,24,40,0.5)", backdropFilter: "blur(2px)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5000,
};
export const modalStyle = {
  width: "90%", background: T.white, borderRadius: T.radius, boxShadow: T.shadowMd, padding: 26,
  fontFamily: T.fontBody, animation: "plc-fade-in .16s ease",
};
export const primaryBtnStyle = {
  background: T.primary, color: "#fff", border: "none", borderRadius: 9, padding: "10px 20px",
  fontSize: 13.5, fontWeight: 600, fontFamily: T.fontBody, cursor: "pointer",
};
export const textBtnStyle = {
  background: "transparent", border: "none", color: T.ink2, fontSize: 13.5, fontWeight: 600,
  cursor: "pointer", fontFamily: T.fontBody, padding: "10px 6px",
};
export const inputStyle = {
  width: "100%", border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px",
  fontSize: 13.5, color: T.ink1, fontFamily: T.fontBody, outline: "none",
};
export const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 600, color: T.ink2, marginBottom: 6,
};
